import { useEffect, useState } from "react";
import { auth, db } from "../firebase/firebaseConfig";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { useRecoilState } from "recoil";
import { isAuthenticatedState } from "../atom/atom.auth";
import { useToast } from "../hooks/useToast";
import { useNavigate, useLocation } from "react-router-dom";

const SESSION_KEY = "auth_session";

interface SessionData {
  isAuthenticated: boolean;
  role: string;
  uid: string;
}

function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isAuthenticated, setIsAuthenticated] =
    useRecoilState(isAuthenticatedState);
  const [isLoading, setIsLoading] = useState(true);

  const { fireToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const checkUserRole = async (uid: string): Promise<string | null> => {
    try {
      const userDoc = await getDoc(doc(db, "User", uid));
      const userData = userDoc.data();
      return userData?.role || null;
    } catch (error) {
      console.error("Role check error:", error);
      return null;
    }
  };

  const updateAuthState = async (uid: string) => {
    const role = await checkUserRole(uid);

    if (role === "admin") {
      setIsAuthenticated(true);
      const sessionData: SessionData = {
        isAuthenticated: true,
        role: role,
        uid: uid,
      };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));

      const queryString = location.search;
      const previousPath =
        sessionStorage.getItem("previousPath") || "/admin/guide";

      if (queryString.includes("isTester=True")) {
        navigate(`/admin/songRequest2${queryString}`, { replace: true });
      } else {
        navigate(`${previousPath}${queryString}`, { replace: true });
      }

      fireToast({ content: "어드민으로 로그인되었습니다." });
    } else {
      setIsAuthenticated(false);
      sessionStorage.removeItem(SESSION_KEY);
      fireToast({ content: "관리자 권한이 없습니다." });
      navigate(`/admin/login${location.search}`);
    }
  };

  const handleLogin = async () => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email + "@gmail.com",
        password
      );

      if (location.pathname !== "/admin/login") {
        sessionStorage.setItem("previousPath", location.pathname);
      }

      await updateAuthState(userCredential.user.uid);
    } catch (error) {
      fireToast({ content: "로그인 실패: " + error });
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsAuthenticated(false);
      sessionStorage.removeItem(SESSION_KEY);
      fireToast({ content: "로그아웃 되었습니다." });
      navigate("/admin/login");
    } catch (error) {
      fireToast({ content: "로그아웃 실패: " + error });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      // 1. 먼저 현재 URL 저장
      if (location.pathname !== "/admin/login") {
        sessionStorage.setItem("previousPath", location.pathname);
      }

      const sessionAuth = sessionStorage.getItem(SESSION_KEY);
      if (sessionAuth) {
        const session: SessionData = JSON.parse(sessionAuth);
        const currentRole = await checkUserRole(session.uid);
        if (currentRole === "admin") {
          setIsAuthenticated(true);
          if (location.pathname === "/admin/login") {
            const previousPath = sessionStorage.getItem("previousPath");
            // 이전 경로가 있고 로그인 페이지가 아닌 경우에만 이동
            if (previousPath && previousPath !== "/admin/login") {
              navigate(`${previousPath}${location.search}`, { replace: true });
            } else {
              navigate(`/admin/guide${location.search}`, { replace: true });
            }
          }
        } else {
          sessionStorage.removeItem(SESSION_KEY);
          setIsAuthenticated(false);
          if (location.pathname !== "/admin/login") {
            navigate(`/admin/login${location.search}`, { replace: true });
          }
        }
      }

      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user) {
          const sessionAuth = sessionStorage.getItem(SESSION_KEY);
          if (!sessionAuth) {
            if (location.pathname !== "/admin/login") {
              sessionStorage.setItem("previousPath", location.pathname);
            }
            await updateAuthState(user.uid);
          }
        } else {
          setIsAuthenticated(false);
          sessionStorage.removeItem(SESSION_KEY);
          if (location.pathname !== "/admin/login") {
            sessionStorage.setItem("previousPath", location.pathname);
            navigate(`/admin/login${location.search}`, { replace: true });
          }
        }
      });

      setIsLoading(false);
      return () => unsubscribe();
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    if (isAuthenticated && location.pathname === "/admin/login") {
      const previousPath =
        sessionStorage.getItem("previousPath") || "/admin/guide";
      navigate(`${previousPath}${location.search}`);
    }
  }, [isAuthenticated, location.pathname]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      {isAuthenticated ? (
        <div>
          <h1>관리자로 로그인됨</h1>
          <button onClick={handleLogout}>로그아웃</button>
        </div>
      ) : (
        <div>
          <h1>관리자 로그인</h1>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={handleKeyDown}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={handleKeyDown}
          />
          <button onClick={handleLogin}>로그인</button>
        </div>
      )}
    </div>
  );
}

export default LoginPage;
